import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";

import { membershipReports } from "../api";
import { authenticationService } from "../api/authentication.service";
import { userService } from "../api/user.service";
import "../assets/styles/pages/multiReports.scss";
import MainStructure from "../components/containers/MainStructure";
import ModelsModal from "../components/NewPedigree/ModelsModal";
import Step1 from "../components/NewPremium/Step1";
import Step2 from "../components/NewPremium/Step2";
import SourAlert from "../components/elements/SourAlert";
import { UserContext } from "../contexts/UserContext";
import useSourAlert from "../hooks/useSourAlert";

function NewPremium() {
	const navigate = useNavigate();
	const user = useContext(UserContext);
	const [isOpenModels, setIsOpenModels] = useState(false);
	const [idProduct, setIdProduct] = useState(0);
	const [pedigreeData, setPedigreeData] = useState({ ml_model: "1" });
	const [credits, setCredits] = useState(0);
	const [combinations, setCombinations] = useState(0);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [alertProps, showAlert] = useSourAlert();

	function handleReportClick() {
		if (!idProduct) {
			showAlert({
				type: "error",
				title: "Pricing unavailable",
				description: "Credit pricing is still loading. Please wait a moment and try again.",
			});
			return;
		}
		const availableCredits = user?.credits ?? 0;
		if (availableCredits < credits) {
			showAlert({
				type: "error",
				title: "Insufficient credits",
				description: `This request needs ${credits} credits. You currently have ${availableCredits}.`,
				showCancelButton: true,
				cancelButtonText: "Buy credits",
				onCancel: () => navigate("/membership"),
			});
			return;
		}

		showAlert({
			type: "warning",
			title: `Confirm ${combinations} combinations`,
			description: `${credits} credits will be deducted to analyse every selected sire × dam pairing.`,
			showCancelButton: true,
			cancelButtonText: "Cancel",
			showOkButton: true,
			okButtonText: "Generate reports",
			onOk: () => generateReport({
				metadata: pedigreeData,
				product: idProduct,
				product_type: "premium",
			}),
		});
	}

	function generateReport(body) {
		setIsSubmitting(true);
		membershipReports
			.create_report(body)
			.then(() => {
				setIsSubmitting(false);
				window.dispatchEvent(new Event("marketing:events-ready"));
				showAlert({
					type: "success",
					title: "Multi report started",
					description:
						"Your combinations are being analysed. Explore results and compare horses from Multi reports.",
					okButtonText: "View report history",
					onOk: () => {
						userService
							.details()
							.then((updatedUser) => authenticationService.updateUserDetails(updatedUser))
							.finally(() => navigate("/premium"));
					},
				});
			})
			.catch(() => {
				setIsSubmitting(false);
				showAlert({
					type: "error",
					title: "Reports not started",
					description: "We could not start this multi report. Please try again.",
					okButtonText: "OK",
				});
			});
	}

	return (
		<MainStructure>
			<main className="multi-builder">
				<header className="multi-builder__hero">
					<p className="multi-builder__eyebrow">Batch pedigree analysis</p>
					<h1>Create a multi report</h1>
					<p>Select sires and dams once. We will analyse every possible pairing in the same request.</p>
				</header>

				<ol className="multi-builder__guide" aria-label="Multi report setup">
					<li><span>1</span><div><strong>Report setup</strong><small>Name the request and choose a model</small></div></li>
					<li><span>2</span><div><strong>Build combinations</strong><small>Add sires and dams or import a saved list</small></div></li>
					<li><span>3</span><div><strong>Generate</strong><small>Review combinations and total credits</small></div></li>
				</ol>

				<Step1
					selected={pedigreeData}
					handleStep1Change={(newData) => setPedigreeData((previous) => ({ ...previous, ...newData }))}
					setIsOpenModels={setIsOpenModels}
					setCredits={setCredits}
					setIdProduct={setIdProduct}
					credits={credits}
					combinations={combinations}
				/>
				<Step2
					handleReportClick={handleReportClick}
					setCombinations={setCombinations}
					setPedigreeData={setPedigreeData}
					pedigreeData={pedigreeData}
					credits={credits}
					isSubmitting={isSubmitting}
					pricingReady={Boolean(idProduct)}
				/>
			</main>
			<ModelsModal isOpenModels={isOpenModels} setIsOpenModels={setIsOpenModels} />
			<SourAlert {...alertProps} />
		</MainStructure>
	);
}

export default NewPremium;
